// EXPERIMENT: application-result-referral-ask.
// Owner: doug. PostHog flag `application-result-referral-ask`, control / test
// at 50 / 50, launched before the code shipped so it starts the moment this
// deploys.
//
// Hypothesis: the moment a student gets their application result is the
// moment they are thinking hardest about the program. Students who can't
// start yet (rejected, waitlisted) or can only start the online course
// (on hold) still know people who could qualify. Asking them right then
// produces referred signups without slowing down their own path.
//
//   control — today's screens: the "You are eligible!" acknowledgement
//             slide with two required checkboxes (on hold), the disabled
//             account screen (rejected), the waitlist card (no_match).
//   test    — a referral ask first ("Get a friend free training.", $150,
//             Send my link / Not now), then:
//               on hold  → a "You're in. Start training now." slide that
//                          lists each barrier as a step to clear while they
//                          train (replaces the acknowledgement slide);
//               rejected → today's disabled account screen;
//               waitlist → today's waitlist card.
//
// ELIGIBILITY (all must hold, see `isApplicationResultReferralEligible`):
//   - signed up on or after APPLICATION_RESULT_REFERRAL_LAUNCH_AT (new
//     signups only; older accounts that hit these screens never see it),
//   - CDL vocation (the program areas named in the copy are CDL programs),
//   - NOT in custody. Hard rule: in-custody students are never exposed and
//     never see a referral ask. Checked two ways: `isInCustody` on the signup
//     status (all three in-custody values) AND the application's "currently
//     incarcerated" answer, because the signup status is only collected
//     through pre-release cohort links.
// Exposure fires only for eligible students who reach one of the three
// result surfaces, so the flag is never evaluated for anyone else.
//
// METRICS: referred signups per exposed student (referredByUserId on the
// referred account, joined to the referrer's arm), share opens on the ask,
// and for on-hold students the guardrails: first lesson completed and
// success plan completed (`Success Plan: Plan Review Completed`).
//
// READ THE READOUT IN POSTHOG ONLY. The shared variant hook falls back to
// control after 3s if PostHog never answers; that fallback is synced to
// Amplitude as if it were a real assignment.
//
// TO KILL: set the PostHog flag to 100% `control`; no deploy needed.

import { isBefore, isValid, parseISO } from "date-fns";
import { FeatureFlagKey } from "src/hooks/useInstrumentation";
import { isV2Application } from "src/pages/BackgroundSurveyScreen/types/ApplicationWizardTypes";
import { UserAccount } from "src/types/User";
import { isCdl, resolveVocationId } from "src/types/Vocation";
import isInCustody from "src/utils/isInCustody";

export const APPLICATION_RESULT_REFERRAL_FLAG_KEY: FeatureFlagKey =
  "application-result-referral-ask";

export const APPLICATION_RESULT_REFERRAL_TEST_VARIANT = "test";

/** Accounts created before this never enter the experiment. */
export const APPLICATION_RESULT_REFERRAL_LAUNCH_AT = "2026-10-06T00:00:00Z";

/** Where the ask was shown. Carried on every event. */
export type ApplicationResultSurface = "on_hold" | "rejected" | "waitlist";

/**
 * In custody by either signal. `incarcerationStatus` is only asked at signup
 * through a pre-release cohort link, so someone in custody who signs up
 * through any other link only shows up in their application answer.
 */
function isInCustodyBySignupOrApplication(user: UserAccount): boolean {
  if (isInCustody(user.incarcerationStatus)) return true;
  return (
    !!user.application &&
    isV2Application(user.application) &&
    user.application.criminalRecord?.isCurrentlyIncarcerated === true
  );
}

export function isApplicationResultReferralEligible(
  user: UserAccount
): boolean {
  if (isInCustodyBySignupOrApplication(user)) return false;
  if (!isCdl(resolveVocationId(user.vocationId))) return false;

  const createdAt = parseISO(user.createdAt ?? "");
  if (!isValid(createdAt)) return false;
  return !isBefore(createdAt, parseISO(APPLICATION_RESULT_REFERRAL_LAUNCH_AT));
}
